import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Button,
  Tooltip,
  TextField,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextareaAutosize,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CardActions,
  useMediaQuery,
  useTheme as useMuiTheme,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  Cancel as RejectIcon,
  ContactPhone as ContactIcon,
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
  Assignment as DocumentIcon,
  Feedback as FeedbackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WorkOutline as HireIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PersonRemove as CancelHireIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { buscarCandidatosProcessados, CandidatoProcessado } from '../../services/vaga';
import { normalizarNome, normalizarEmail, normalizarTelefone } from '../../utils/normalization';
import { downloadCurriculo, verificarCurriculo } from '../../services/candidato';
import axios from 'axios';

const CandidatosAprovados: React.FC = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [candidatos, setCandidatos] = useState<CandidatoProcessado[]>([]);
  const [candidatosFiltrados, setCandidatosFiltrados] = useState<CandidatoProcessado[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  const [observacoes, setObservacoes] = useState<{[key: number]: string}>({});
  const [checkboxes, setCheckboxes] = useState<{[key: number]: {[key: string]: boolean}}>({});
  const [expandidos, setExpandidos] = useState<{[key: number]: boolean}>({});
  const [candidatosContratados, setCandidatosContratados] = useState<Set<number>>(new Set());
  const [candidatosArquivados, setCandidatosArquivados] = useState<Set<number>>(new Set());
  const [statusOrigemArquivados, setStatusOrigemArquivados] = useState<Map<number, 'APROVADO' | 'REJEITADO' | 'CONTRATADO'>>(new Map());
  const [observacoesSalvas, setObservacoesSalvas] = useState<{[key: number]: { status: 'success' | 'error', timestamp: number }}>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  
  // Estados dos filtros de exibição
  const [filtroStatus, setFiltroStatus] = useState<"aprovados" | "contratados" | "rejeitados" | "arquivados">("aprovados");
  
  // Estados para paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Modal de confirmação para arquivar
  const [modalArquivar, setModalArquivar] = useState(false);
  const [candidatoParaArquivar, setCandidatoParaArquivar] = useState<CandidatoProcessado | null>(null);
  
  // Modal de confirmação para cancelar rejeição
  const [modalCancelarRejeicao, setModalCancelarRejeicao] = useState(false);
  const [candidatoParaCancelarRejeicao, setCandidatoParaCancelarRejeicao] = useState<CandidatoProcessado | null>(null);
  
  // Modal para exibir dados do candidato
  const [modalDadosCandidato, setModalDadosCandidato] = useState(false);
  const [candidatoSelecionado, setCandidatoSelecionado] = useState<CandidatoProcessado | null>(null);

  // Modal de confirmação de contratação
  const [modalConfirmacaoContratacao, setModalConfirmacaoContratacao] = useState(false);
  const [candidatoParaContratar, setCandidatoParaContratar] = useState<CandidatoProcessado | null>(null);

  // Estado para forçar re-render das contagens
  const [contadorRerender, setContadorRerender] = useState(0);

  // Estado para verificar se candidato tem currículo
  const [possuiCurriculo, setPossuiCurriculo] = useState<boolean>(false);
  const [verificandoCurriculo, setVerificandoCurriculo] = useState<boolean>(false);

  const showMessage = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleBaixarCurriculo = async (candidatoId: number, nomeCandidato: string) => {
    try {
      const blob = await downloadCurriculo(candidatoId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `curriculo_${nomeCandidato.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showMessage('Currículo baixado com sucesso!', 'success');
    } catch (error) {
      showMessage('Erro ao baixar currículo. O candidato pode não ter enviado o documento.', 'error');
    }
  };

  const formatarTelefoneParaWhatsApp = (telefone: string): string => {
    // Remove todos os caracteres não numéricos
    const numeros = telefone.replace(/\D/g, '');
    
    // Se tiver 11 dígitos (com 9 na frente), retorna 55 + DDD + número
    // Se tiver 10 dígitos (sem 9), retorna 55 + DDD + número
    return `55${numeros}`;
  };

  const abrirWhatsApp = (telefone: string) => {
    if (!telefone) return;
    
    const telefoneFormatado = formatarTelefoneParaWhatsApp(telefone);
    const url = `https://wa.me/${telefoneFormatado}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    carregarCandidatos();
  }, []);

  // Verificar se o candidato tem currículo quando abrir o modal
  useEffect(() => {
    const verificarCurriculoCandidato = async () => {
      if (candidatoSelecionado && modalDadosCandidato) {
        setVerificandoCurriculo(true);
        try {
          const temCurriculo = await verificarCurriculo(candidatoSelecionado.idCandidato);
          setPossuiCurriculo(temCurriculo);
        } catch (error) {
          console.error('Erro ao verificar currículo:', error);
          setPossuiCurriculo(false);
        } finally {
          setVerificandoCurriculo(false);
        }
      } else {
        setPossuiCurriculo(false);
      }
    };

    verificarCurriculoCandidato();
  }, [candidatoSelecionado, modalDadosCandidato]);

  useEffect(() => {
    filtrarCandidatos();
  }, [termoBusca, candidatos]);

  const carregarCandidatos = async () => {
    setLoading(true);
    setErro('');
    
    try {
      // Buscar todos os tipos de candidatos do backend
      const { buscarCandidatosProcessados, buscarCandidatosContratados, buscarCandidatosArquivados } = await import('../../services/vaga');
      
      const [processadosResult, contratadosResult, arquivadosResult] = await Promise.all([
        buscarCandidatosProcessados(),
        buscarCandidatosContratados(),
        buscarCandidatosArquivados()
      ]);

      // Combinar todos os candidatos e remover duplicados por ID de candidatura
      const candidatosMap = new Map();
      
      // Adicionar processados
      processadosResult.data.forEach(c => candidatosMap.set(c.id, c));
      
      // Adicionar contratados (sobrescreve se já existir)
      contratadosResult.data.forEach(c => candidatosMap.set(c.id, c));
      
      // Adicionar arquivados (sobrescreve se já existir)
      arquivadosResult.data.forEach(c => candidatosMap.set(c.id, c));
      
      const todosContdidatos = Array.from(candidatosMap.values());

      setCandidatos(todosContdidatos);

      // Identificar quais candidatos estão contratados (baseado no status do backend)
      const idsContratados = contratadosResult.data.map(c => c.id);
      setCandidatosContratados(new Set(idsContratados));

      // Identificar quais candidatos estão arquivados (baseado nos dados do backend)
      const idsArquivados = arquivadosResult.data.map(c => c.id);
      setCandidatosArquivados(new Set(idsArquivados));

      // Criar mapa de status original dos arquivados (do banco de dados)
      const statusMap = new Map<number, 'APROVADO' | 'REJEITADO' | 'CONTRATADO'>();
      arquivadosResult.data.forEach(candidato => {
        // O status vem do banco de dados
        statusMap.set(candidato.id, candidato.status as 'APROVADO' | 'REJEITADO' | 'CONTRATADO');
      });
      setStatusOrigemArquivados(statusMap as any);

      // Carregar observações dos candidatos (se o backend retornar)
      const observacoesMap: {[key: number]: string} = {};
      todosContdidatos.forEach(candidato => {
        if (candidato.observacoes) {
          observacoesMap[candidato.id] = candidato.observacoes;
        }
      });
      setObservacoes(observacoesMap);
      
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar candidatos');
      console.error('Erro ao carregar candidatos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtrarCandidatos = () => {
    if (!termoBusca.trim()) {
      setCandidatosFiltrados(candidatos);
      return;
    }

    const termo = termoBusca.toLowerCase();
    const filtrados = candidatos.filter(candidato =>
      candidato.candidatoNome.toLowerCase().includes(termo) ||
      candidato.candidatoEmail.toLowerCase().includes(termo) ||
      candidato.vagaNome.toLowerCase().includes(termo)
    );
    
    setCandidatosFiltrados(filtrados);
  };

  const atualizarObservacao = (candidatoId: number, observacao: string) => {
    setObservacoes(prev => ({
      ...prev,
      [candidatoId]: observacao
    }));
  };

  const salvarObservacao = async (candidatoId: number) => {
    try {
      const observacao = observacoes[candidatoId] || '';
      
      // Salvar no backend (backend usa 'anotacoes' como nome do campo)
      const { getApiBaseUrl } = await import('../../services/baseUrl');
      const apiUrl = getApiBaseUrl();
      await axios.put(`${apiUrl}/candidaturas/${candidatoId}`, {
        anotacoes: observacao
      }, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      setObservacoesSalvas(prev => ({
        ...prev,
        [candidatoId]: { status: 'success', timestamp: Date.now() }
      }));
      
      // Limpar o aviso após 3 segundos
      setTimeout(() => {
        setObservacoesSalvas(prev => {
          const novo = { ...prev };
          delete novo[candidatoId];
          return novo;
        });
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao salvar observação no banco de dados:', error);
      
      setObservacoesSalvas(prev => ({
        ...prev,
        [candidatoId]: { status: 'error', timestamp: Date.now() }
      }));
      
      // Limpar o aviso após 3 segundos
      setTimeout(() => {
        setObservacoesSalvas(prev => {
          const novo = { ...prev };
          delete novo[candidatoId];
          return novo;
        });
      }, 3000);
    }
  };

  const atualizarCheckbox = (candidatoId: number, checkboxKey: string, checked: boolean) => {
    setCheckboxes(prev => ({
      ...prev,
      [candidatoId]: {
        ...prev[candidatoId],
        [checkboxKey]: checked
      }
    }));
  };

  const alternarExpansao = (candidatoId: number) => {
    setExpandidos(prev => ({
      ...prev,
      [candidatoId]: !prev[candidatoId]
    }));
  };

  const handleContratarCandidato = (candidato: CandidatoProcessado) => {
    setCandidatoParaContratar(candidato);
    setModalConfirmacaoContratacao(true);
  };

  const confirmarContratacao = async () => {
    if (candidatoParaContratar) {
      try {
        // Usar endpoint do backend para contratar
        const { contratarCandidato } = await import('../../services/vaga/aprovacoes');
        await contratarCandidato(candidatoParaContratar.id);

        // Disparar evento para atualizar dashboard
        window.dispatchEvent(new CustomEvent('candidatoContratado', {
          detail: { candidatoId: candidatoParaContratar.id }
        }));

        // Recarregar candidatos do backend
        await carregarCandidatos();

        // Forçar re-render das contagens
        setContadorRerender(prev => prev + 1);

        // Mostrar mensagem de sucesso
        showMessage(`Candidato ${candidatoParaContratar.candidatoNome} contratado com sucesso!`, 'success');

        // Fechar modal e limpar estado
        setModalConfirmacaoContratacao(false);
        setCandidatoParaContratar(null);
      } catch (error) {
        showMessage('Erro ao contratar candidato. Tente novamente.', 'error');
        console.error('Erro ao contratar:', error);
      }
    }
  };

  const cancelarContratacao = () => {
    setModalConfirmacaoContratacao(false);
    setCandidatoParaContratar(null);
  };

  const handleArquivarCandidato = (candidato: CandidatoProcessado) => {
    setCandidatoParaArquivar(candidato);
    setModalArquivar(true);
  };
  
  const confirmarArquivar = async () => {
    if (candidatoParaArquivar) {
      try {
        // Usar endpoint do backend para arquivar
        const { arquivarCandidato } = await import('../../services/vaga/aprovacoes');
        await arquivarCandidato(candidatoParaArquivar.id);

        // Disparar evento para atualizar contagens
        window.dispatchEvent(new CustomEvent('candidatoArquivado', {
          detail: { candidatoId: candidatoParaArquivar.id }
        }));

        // Recarregar candidatos do backend
        await carregarCandidatos();

        // Forçar re-render das contagens
        setContadorRerender(prev => prev + 1);

        setModalArquivar(false);
        setCandidatoParaArquivar(null);

        showMessage('Candidato arquivado com sucesso!', 'success');
      } catch (error) {
        showMessage('Erro ao arquivar candidato. Tente novamente.', 'error');
        console.error('Erro ao arquivar:', error);
      }
    }
  };
  
  const cancelarArquivar = () => {
    setModalArquivar(false);
    setCandidatoParaArquivar(null);
  };
  
  const handleCancelarRejeicao = (candidato: CandidatoProcessado) => {
    setCandidatoParaCancelarRejeicao(candidato);
    setModalCancelarRejeicao(true);
  };

  const confirmarCancelarRejeicao = async () => {
    if (!candidatoParaCancelarRejeicao) return;
    
    try {
      
      // Chamar API para cancelar rejeição (volta status para PENDENTE)
      const { cancelarRejeicao } = await import('../../services/vaga/aprovacoes');
      await cancelarRejeicao(candidatoParaCancelarRejeicao.id);
      
      // Remover candidato da lista local (ele volta para aprovações)
      setCandidatos(prev => prev.filter(c => c.id !== candidatoParaCancelarRejeicao.id));
      
      // Disparar evento para atualizar contagens na sidebar (aprovações pendentes)
      window.dispatchEvent(new CustomEvent('candidaturaProcessada'));
      
      // Forçar re-render das contagens
      setContadorRerender(prev => prev + 1);
      
      
      // Fechar modal
      setModalCancelarRejeicao(false);
      setCandidatoParaCancelarRejeicao(null);
      
    } catch (error) {
      showMessage('Erro ao cancelar rejeição. Tente novamente.', 'error');
    }
  };

  const cancelarModalRejeicao = () => {
    setModalCancelarRejeicao(false);
    setCandidatoParaCancelarRejeicao(null);
  };
  
  const handleExibirDados = (candidato: CandidatoProcessado) => {
    setCandidatoSelecionado(candidato);
    setModalDadosCandidato(true);
  };

  
  const handleDesarquivarCandidato = async (candidatoId: number) => {
    try {
      // Usar endpoint do backend para desarquivar
      const { desarquivarCandidato } = await import('../../services/vaga/aprovacoes');
      await desarquivarCandidato(candidatoId);

      // Recarregar candidatos do backend
      await carregarCandidatos();

      showMessage('Candidato desarquivado com sucesso!', 'success');

      // Disparar evento para atualizar contagens
      window.dispatchEvent(new CustomEvent('candidatoDesarquivado', {
        detail: { candidatoId }
      }));
    } catch (error) {
      showMessage('Erro ao desarquivar candidato. Tente novamente.', 'error');
      console.error('Erro ao desarquivar:', error);
    }
  };

  const handleCancelarContratacao = async (candidato: CandidatoProcessado) => {
    try {
      // Cancelar contratação no backend (volta para APROVADO)
      const { cancelarContratacao } = await import('../../services/vaga/aprovacoes');
      await cancelarContratacao(candidato.id);

      // Recarregar candidatos do backend
      await carregarCandidatos();

      showMessage('Contratação cancelada com sucesso! Candidato voltou para status APROVADO.', 'success');

      // Disparar evento para atualizar dashboard
      window.dispatchEvent(new CustomEvent('candidatoContratacaoCancelada', {
        detail: { candidatoId: candidato.id }
      }));
    } catch (error) {
      showMessage('Erro ao cancelar contratação. Tente novamente.', 'error');
      console.error('Erro ao cancelar contratação:', error);
    }
  };

  const handleRejeitarCandidato = async (candidato: CandidatoProcessado) => {
    try {
      // Usar endpoint do backend para rejeitar
      const { rejeitarCandidatura } = await import('../../services/vaga/aprovacoes');
      await rejeitarCandidatura(candidato.id);

      // Disparar evento para atualizar contagens
      window.dispatchEvent(new CustomEvent('candidatoRejeitado', {
        detail: { candidatoId: candidato.id }
      }));

      // Recarregar candidatos do backend
      await carregarCandidatos();

      // Forçar re-render das contagens
      setContadorRerender(prev => prev + 1);

      showMessage('Candidato rejeitado com sucesso!', 'success');
    } catch (error) {
      showMessage('Erro ao rejeitar candidato. Tente novamente.', 'error');
      console.error('Erro ao rejeitar candidato:', error);
    }
  };
  
  const fecharModalDados = () => {
    setModalDadosCandidato(false);
    setCandidatoSelecionado(null);
  };

  // Funções para calcular contagens (com dependência do contadorRerender para forçar atualização)
  const getContagemAprovados = () => {
    void contadorRerender; // Dependência para forçar re-render
    const aprovados = candidatos.filter(c => c.status === 'APROVADO' && !candidatosArquivados.has(c.id) && !candidatosContratados.has(c.id));
    return aprovados.length;
  };

  const getContagemRejeitados = () => {
    void contadorRerender; // Dependência para forçar re-render
    const rejeitados = candidatos.filter(c => c.status === 'REJEITADO' && !candidatosArquivados.has(c.id));
    return rejeitados.length;
  };

  const getContagemContratados = () => {
    void contadorRerender; // Dependência para forçar re-render
    return candidatosContratados.size;
  };

  const getContagemArquivados = () => {
    void contadorRerender; // Dependência para forçar re-render
    return candidatosArquivados.size;
  };

  // Função para calcular candidatos visíveis baseado nos filtros
  const getCandidatosVisiveis = () => {
    let candidatos = candidatosFiltrados || [];

    let aprovadosVisiveis: CandidatoProcessado[] = [];
    let contratadosVisiveis: CandidatoProcessado[] = [];
    let rejeitadosVisiveis: CandidatoProcessado[] = [];
    let arquivadosVisiveis: CandidatoProcessado[] = [];

    // Separar candidatos arquivados dos outros (baseado no Set de IDs arquivados)
    const candidatosArquivadosLista = candidatos.filter(c => candidatosArquivados.has(c.id));
    const candidatosAtivos = candidatos.filter(c => !candidatosArquivados.has(c.id));
    
      // Mostrar apenas APROVADOS (status do banco = 'APROVADO')
      aprovadosVisiveis = candidatosAtivos.filter(c => c.status === 'APROVADO');
    
      // Mostrar candidatos CONTRATADOS (status do banco = 'CONTRATADO')
      contratadosVisiveis = candidatosAtivos.filter(c => c.status === 'CONTRATADO');
    
      // Mostrar REJEITADOS (status do banco = 'REJEITADO')
      rejeitadosVisiveis = candidatosAtivos.filter(c => c.status === 'REJEITADO');
    
      // Mostrar arquivados (ativa = 'N' no banco, independente do status)
      arquivadosVisiveis = candidatosArquivadosLista;
    
    return {
      aprovados: aprovadosVisiveis,
      contratados: contratadosVisiveis,
      rejeitados: rejeitadosVisiveis,
      arquivados: arquivadosVisiveis,
      total: aprovadosVisiveis.length + contratadosVisiveis.length + rejeitadosVisiveis.length + arquivadosVisiveis.length
    };
  };

  // Função para obter candidatos do status selecionado
  const getCandidatosPorStatus = () => {
    const candidatosVisiveis = getCandidatosVisiveis();
    switch (filtroStatus) {
      case 'aprovados':
        return candidatosVisiveis.aprovados;
      case 'contratados':
        return candidatosVisiveis.contratados;
      case 'rejeitados':
        return candidatosVisiveis.rejeitados;
      case 'arquivados':
        return candidatosVisiveis.arquivados;
      default:
        return candidatosVisiveis.aprovados;
    }
  };

  // Função para obter contagem de candidatos por status
  const getContagemPorStatus = () => {
    const candidatosVisiveis = getCandidatosVisiveis();
    return {
      aprovados: candidatosVisiveis.aprovados.length,
      contratados: candidatosVisiveis.contratados.length,
      rejeitados: candidatosVisiveis.rejeitados.length,
      arquivados: candidatosVisiveis.arquivados.length
    };
  };

  const formatarTelefone = (telefone: string) => {
    const numeros = telefone?.replace(/\D/g, '') || '';
    if (numeros.length === 11) {
      return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return telefone;
  };

  const getInitials = (nome: string) => {
    if (!nome || typeof nome !== 'string') {
      return '??';
    }
    return nome
      .split(' ')
      .filter(n => n.length > 0) // Remove strings vazias
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Função para obter status detalhado de candidatos arquivados
  const getStatusDetalhadoArquivado = (candidato: CandidatoProcessado) => {
    if (filtroStatus !== 'arquivados') {
      return candidato.status;
    }
    
    // Para candidatos arquivados, mostrar o status original
    const statusOriginal = statusOrigemArquivados.get(candidato.id);
    if (statusOriginal) {
      return `Arquivado (${statusOriginal.toLowerCase()})`;
    }
    
    return 'Arquivado';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Cabeçalho */}
      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: 700, mb: 1 }}
      >
          Gerenciar Candidatos
        </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Gerencie seus candidatos - visualize, contrate, rejeite ou arquive
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {erro}
        </Alert>
      )}

      {/* Campo de Busca e Filtros */}
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        {/* Toggle para status dos candidatos */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <ToggleButtonGroup
            value={filtroStatus}
            exclusive
            onChange={(_: any, novoStatus: "aprovados" | "contratados" | "rejeitados" | "arquivados") =>
              novoStatus && setFiltroStatus(novoStatus)
            }
            aria-label="filtro de status dos candidatos"
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="aprovados" aria-label="candidatos aprovados">
              <CheckIcon sx={{ mr: 1 }} />
              Candidatos Aprovados ({getContagemPorStatus().aprovados})
            </ToggleButton>
            <ToggleButton value="contratados" aria-label="candidatos contratados">
              <HireIcon sx={{ mr: 1 }} />
              Candidatos Contratados ({getContagemPorStatus().contratados})
            </ToggleButton>
            <ToggleButton value="rejeitados" aria-label="candidatos rejeitados">
              <RejectIcon sx={{ mr: 1 }} />
              Candidatos Rejeitados ({getContagemPorStatus().rejeitados})
            </ToggleButton>
            <ToggleButton value="arquivados" aria-label="candidatos arquivados">
              <ArchiveIcon sx={{ mr: 1 }} />
              Candidatos Arquivados ({getContagemPorStatus().arquivados})
            </ToggleButton>
          </ToggleButtonGroup>
              </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder={
            isMobile
              ? "Buscar..."
              : "Buscar por nome, email, vaga..."
          }
          value={termoBusca}
          onChange={(e: any) => setTermoBusca(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {termoBusca && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "text.secondary" }}
          >
            Candidatos encontrados: {getCandidatosPorStatus().length}
          </Typography>
        )}
      </Paper>

      {/* Lista de Candidatos */}
      <Paper elevation={3}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {filtroStatus === "aprovados" && <CheckIcon sx={{ color: "primary.main" }} />}
            {filtroStatus === "contratados" && <HireIcon sx={{ color: "success.main" }} />}
            {filtroStatus === "rejeitados" && <RejectIcon sx={{ color: "error.main" }} />}
            {filtroStatus === "arquivados" && <ArchiveIcon sx={{ color: "warning.main" }} />}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {filtroStatus === "aprovados" && "Candidatos Aprovados"}
              {filtroStatus === "contratados" && "Candidatos Contratados"}
              {filtroStatus === "rejeitados" && "Candidatos Rejeitados"}
              {filtroStatus === "arquivados" && "Candidatos Arquivados"}
              {" ("}{getCandidatosPorStatus().length}{")"}
          </Typography>
          </Box>
        </Box>

        {getCandidatosPorStatus().length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <PersonIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
            {candidatos.length === 0 
                ? "Nenhum candidato processado ainda"
                : "Nenhum candidato encontrado para este filtro"}
          </Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              {/* Versão Desktop - Tabela */}
              {!isMobile && (
              <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                      <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Vaga</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Cargo</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: { xs: 100, sm: 140 } }}>
                        Ações
                      </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {getCandidatosPorStatus()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((candidato) => (
                        <TableRow key={candidato.id} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32, fontSize: '0.8rem' }}>
                                {getInitials(candidato.candidatoNome)}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {normalizarNome(candidato.candidatoNome)}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Chip 
                              label={getStatusDetalhadoArquivado(candidato)}
                              size="small"
                              color={
                                filtroStatus === "aprovados" ? "primary" :
                                filtroStatus === "contratados" ? "success" :
                                filtroStatus === "rejeitados" ? "error" :
                                "warning"
                              }
                              icon={
                                filtroStatus === "aprovados" ? <CheckIcon /> :
                                filtroStatus === "contratados" ? <HireIcon /> :
                                filtroStatus === "rejeitados" ? <RejectIcon /> :
                                <ArchiveIcon />
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {normalizarNome(candidato.vagaNome)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {normalizarNome(candidato.cargo)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Tooltip title="Exibir dados">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExibirDados(candidato)}
                                  sx={{ 
                                    color: "#2196f3",
                                    "&:hover": {
                                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {filtroStatus === "aprovados" && (
                                <>
                                  <Tooltip title="Arquivar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleArquivarCandidato(candidato)}
                                  sx={{ 
                                        color: "orange",
                                        "&:hover": {
                                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                                        },
                                  }}
                                >
                                  <ArchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                                  <Tooltip title="Rejeitar">
                                <IconButton
                                  size="small"
                                      onClick={() => handleRejeitarCandidato(candidato)}
                                  sx={{ 
                                        color: "#f44336",
                                        "&:hover": {
                                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                                        },
                                  }}
                                >
                                      <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                                  <Tooltip title="Contratar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleContratarCandidato(candidato)}
                                  sx={{
                                        color: "#4caf50",
                                        "&:hover": {
                                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                                        },
                                  }}
                                >
                                  <HireIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                                </>
                              )}

                              {filtroStatus === "contratados" && (
                                <>
                                  <Tooltip title="Arquivar">
                                    <IconButton
                              size="small"
                                      onClick={() => handleArquivarCandidato(candidato)}
                              sx={{ 
                                        color: "orange",
                                        "&:hover": {
                                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                                        },
                                      }}
                                    >
                                      <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Cancelar contratação">
                            <IconButton
                                      size="small"
                                      onClick={() => handleCancelarContratacao(candidato)}
                              sx={{ 
                                        color: "#f44336",
                                        "&:hover": {
                                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                                        },
                              }}
                            >
                                      <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                                </>
                              )}

                              {filtroStatus === "rejeitados" && (
                                <>
                                  <Tooltip title="Arquivar">
                            <IconButton
                                      size="small"
                              onClick={() => handleArquivarCandidato(candidato)}
                              sx={{ 
                                        color: "orange",
                                        "&:hover": {
                                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                                        },
                              }}
                            >
                                      <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                                  <Tooltip title="Cancelar rejeição">
                            <IconButton
                                      size="small"
                              onClick={() => handleCancelarRejeicao(candidato)}
                              sx={{ 
                                        color: "#f44336",
                                        "&:hover": {
                                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                                        },
                              }}
                            >
                                      <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
            </>
          )}

                              {filtroStatus === "arquivados" && (
                                <>
                                  <Tooltip title="Desarquivar">
                                    <IconButton
                              size="small"
                                      onClick={() => handleDesarquivarCandidato(candidato.id)}
                              sx={{ 
                                        color: "#4caf50",
                                        "&:hover": {
                                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                                        },
                                      }}
                                    >
                                      <UnarchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                                  <Tooltip title="Contratar">
                                <IconButton
                                  size="small"
                                      onClick={() => handleContratarCandidato(candidato)}
                                  sx={{ 
                                        color: "#4caf50",
                                        "&:hover": {
                                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                                        },
                                  }}
                                >
                                      <HireIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Versão Mobile - Cards */}
              {isMobile && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {getCandidatosPorStatus()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((candidato) => (
                    <Card key={candidato.id} elevation={3}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                            {getInitials(candidato.candidatoNome)}
                          </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {normalizarNome(candidato.candidatoNome)}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Status:</strong>{" "}
                            <Chip 
                              label={getStatusDetalhadoArquivado(candidato)}
                              size="small"
                              color={
                                filtroStatus === "aprovados" ? "primary" :
                                filtroStatus === "contratados" ? "success" :
                                filtroStatus === "rejeitados" ? "error" :
                                "warning"
                              }
                            />
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            <strong>Vaga:</strong> {normalizarNome(candidato.vagaNome)}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            <strong>Cargo:</strong> {normalizarNome(candidato.cargo)}
                          </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center" }}>
                            <IconButton
                              size="medium"
                              color="info"
                              onClick={() => handleExibirDados(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'info.light', opacity: 0.1 }
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>

                          {filtroStatus === "aprovados" && (
                            <>
                            <IconButton
                              size="medium"
                              color="warning"
                              onClick={() => handleArquivarCandidato(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'warning.light', opacity: 0.1 }
                              }}
                            >
                              <ArchiveIcon />
                            </IconButton>

                            <IconButton
                              size="medium"
                              color="error"
                                onClick={() => handleRejeitarCandidato(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'error.light', opacity: 0.1 }
                              }}
                            >
                                <RejectIcon />
                            </IconButton>

                                <IconButton
                                size="medium"
                                color="success"
                                onClick={() => handleContratarCandidato(candidato)}
                                  sx={{ 
                                  '&:hover': { backgroundColor: 'success.light', opacity: 0.1 }
                                  }}
                                >
                                <HireIcon />
                                </IconButton>
                            </>
                          )}

                          {filtroStatus === "contratados" && (
                            <>
                                <IconButton
                                size="medium"
                                  color="warning"
                                  onClick={() => handleArquivarCandidato(candidato)}
                                  sx={{ 
                                    '&:hover': { backgroundColor: 'warning.light', opacity: 0.1 }
                                  }}
                                >
                                <ArchiveIcon />
                                </IconButton>

                                <IconButton
                                size="medium"
                                  color="error"
                                onClick={() => handleCancelarContratacao(candidato)}
                                  sx={{ 
                                    '&:hover': { backgroundColor: 'error.light', opacity: 0.1 }
                                  }}
                                >
                                <UndoIcon />
                                </IconButton>
                            </>
                          )}

                          {filtroStatus === "rejeitados" && (
                            <>
                            <IconButton
                              size="medium"
                              color="warning"
                              onClick={() => handleArquivarCandidato(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'warning.light', opacity: 0.1 }
                              }}
                            >
                              <ArchiveIcon />
                            </IconButton>

                            <IconButton
                              size="medium"
                              color="error"
                              onClick={() => handleCancelarRejeicao(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'error.light', opacity: 0.1 }
                              }}
                            >
                              <UndoIcon />
                            </IconButton>
            </>
          )}
          
                          {filtroStatus === "arquivados" && (
                            <>
                              <IconButton
                                size="medium"
                                color="success"
                                onClick={() => handleDesarquivarCandidato(candidato.id)}
                                sx={{ 
                                  '&:hover': { backgroundColor: 'success.light', opacity: 0.1 }
                                }}
                              >
                                <UnarchiveIcon />
                              </IconButton>

                            <IconButton
                              size="medium"
                              color="success"
                                onClick={() => handleContratarCandidato(candidato)}
                              sx={{ 
                                '&:hover': { backgroundColor: 'success.light', opacity: 0.1 }
                              }}
                            >
                                <HireIcon />
                            </IconButton>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

            <TablePagination
              component={Paper}
              elevation={1}
              sx={{ mt: 2 }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={getCandidatosPorStatus().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e: any, newPage: number) => setPage(newPage)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={isMobile ? "Por página:" : "Linhas por página:"}
              labelDisplayedRows={({ from, to, count }: any) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Modal de confirmação de contratação */}
      <Dialog open={modalConfirmacaoContratacao} onClose={cancelarContratacao}>
        <DialogTitle>Confirmar Contratação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja contratar o candidato {candidatoParaContratar?.candidatoNome}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarContratacao} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmarContratacao} color="primary" variant="contained">
            Confirmar Contratação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de arquivamento */}
      <Dialog open={modalArquivar} onClose={cancelarArquivar}>
        <DialogTitle>Confirmar Arquivamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja arquivar o candidato {candidatoParaArquivar?.candidatoNome}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarArquivar} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmarArquivar} color="primary" variant="contained">
            Confirmar Arquivamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de cancelamento de rejeição */}
      <Dialog open={modalCancelarRejeicao} onClose={cancelarModalRejeicao}>
        <DialogTitle>Cancelar Rejeição</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja cancelar a rejeição do candidato {candidatoParaCancelarRejeicao?.candidatoNome}?
            O candidato voltará para a lista de aprovações pendentes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarModalRejeicao} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmarCancelarRejeicao} color="primary" variant="contained">
            Confirmar Cancelamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de exibição de dados do candidato */}
      <Dialog
        open={modalDadosCandidato}
        onClose={fecharModalDados}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dados do Candidato</DialogTitle>
        <DialogContent>
          {candidatoSelecionado && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {normalizarNome(candidatoSelecionado.candidatoNome)}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {normalizarEmail(candidatoSelecionado.candidatoEmail)}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Telefone:</strong> {formatarTelefone(candidatoSelecionado.candidatoTelefone || '')}
                        </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Vaga:</strong> {normalizarNome(candidatoSelecionado.vagaNome)}
                        </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Cargo:</strong> {normalizarNome(candidatoSelecionado.cargo)}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {candidatoSelecionado.status}
                    </Typography>
                      </Box>

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                {verificandoCurriculo ? (
                  <Button
                    variant="outlined"
                    disabled
                    startIcon={<CircularProgress size={16} />}
                  >
                    Verificando...
                  </Button>
                ) : possuiCurriculo ? (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (candidatoSelecionado.idCandidato) {
                        handleBaixarCurriculo(candidatoSelecionado.idCandidato, candidatoSelecionado.candidatoNome);
                      }
                    }}
                  >
                    Baixar Currículo
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    disabled
                    startIcon={<DownloadIcon />}
                  >
                    Currículo não disponível
                  </Button>
                )}
                
          <Button
            variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    const telefone = candidatoSelecionado.candidatoTelefone?.replace(/\D/g, '');
                    if (telefone) {
                      window.open(`https://wa.me/55${telefone}`, '_blank');
                    }
                  }}
                >
                  WhatsApp
          </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModalDados} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CandidatosAprovados;